import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentDots, faPaperPlane, faXmark } from '@fortawesome/free-solid-svg-icons';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import './ChatBot.scss';

const CHATBOT_URL = 'http://31.97.55.154:3100';

function ChatBot() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([{ text: t('chatbot.welcome'), sender: 'bot', isWelcome: true }]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const socket = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Mettre à jour le message de bienvenue lors du changement de langue
  useEffect(() => {
    setMessages((prev) => prev.map((msg) =>
      msg.isWelcome ? { ...msg, text: t('chatbot.welcome') } : msg
    ));
  }, [t]);

  // Connexion Socket.IO au backend chatbot
  useEffect(() => {
    socket.current = io(CHATBOT_URL);

    socket.current.on('chat_message', (response) => {
      const sanitized = DOMPurify.sanitize(response, { ALLOWED_TAGS: [] });
      setMessages((prev) => [...prev, { text: sanitized, sender: 'bot' }]);
      setIsLoading(false);
    });

    return () => {
      if (socket.current) {
        socket.current.disconnect();
      }
    };
  }, []);

  // Auto-scroll vers le bas à chaque nouveau message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Focus sur l'input à l'ouverture
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;

    setMessages((prev) => [...prev, { text: trimmed, sender: 'user' }]);
    setInput('');
    setIsLoading(true);

    socket.current.emit('chat_message', trimmed);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="ChatBot">
      {/* Fenêtre de chat */}
      {isOpen && (
        <div className="ChatBot_window">
          <div className="ChatBot_header">
            <span className="ChatBot_header_title">{t('chatbot.title')}</span>
            <button className="ChatBot_header_close" onClick={() => setIsOpen(false)}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className="ChatBot_messages">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`ChatBot_message ChatBot_message--${msg.sender}`}
              >
                {msg.text}
              </div>
            ))}
            {isLoading && (
              <div className="ChatBot_message ChatBot_message--bot">
                <div className="ChatBot_typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="ChatBot_input">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('chatbot.placeholder')}
              disabled={isLoading}
            />
            <button
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
            >
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>
        </div>
      )}

      {/* Bouton flottant */}
      <button
        className={`ChatBot_toggle ${isOpen ? 'ChatBot_toggle--open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <FontAwesomeIcon icon={isOpen ? faXmark : faCommentDots} />
      </button>
    </div>
  );
}

export default ChatBot;
